import { ChangeDetectorRef, Component, ElementRef, OnInit, ViewChild } from '@angular/core';
import { Router } from '@angular/router';
import { TranslocoPipe, TranslocoService } from '@jsverse/transloco';
import { CustomButtonComponent } from "../../../../shared/components/custom-button/custom-button.component";
import { ToastComponent } from "../../../../shared/components/toast/toast.component";
import { GenerateElectricityJSONDialogComponent } from "../../dialogs/generate-electricity-jsondialog/generate-electricity-jsondialog.component";
import { GenerateGasJSONDialogComponent } from '../../dialogs/generate-gas-jsondialog/generate-gas-jsondialog.component';
import { InvoiceElectricityData, InvoiceGasData } from '../../../../core/interfaces/invoice-interfaces';
import { CommonModule } from '@angular/common';
import { IsoToFormattedDatePipe } from "../../../../core/pipes/iso-to-formatted-date.pipe";
import { DeleteConfirmationDialogComponent } from "../../../../shared/dialogs/delete-confirmation-dialog/delete-confirmation-dialog.component";
import { IsoToFormattedPeriodPipe } from "../../../../core/pipes/iso-to-formatted-period.pipe";
import { AdminService } from '../../../../core/api/admin.service';
import { HttpErrorResponse } from '@angular/common/http';
import { ApiErrorService } from '../../../../core/api/api-error.service';
import { JSONGeneratorHistoryElectricityGet, JSONGeneratorHistoryGasGet } from '../../../../core/interfaces/admin-interfaces';
import { combineLatest, lastValueFrom } from 'rxjs';
import { CustomDropdownComponent } from "../../../../shared/components/custom-dropdown/custom-dropdown.component";
import { AdminNavigationComponent } from "../../components/admin-navigation/admin-navigation.component";
import { AdminTopbarComponent } from "../../components/admin-topbar/admin-topbar.component";

@Component({
  selector: 'app-admin-json-generator-page',
  standalone: true,
  imports: [TranslocoPipe, CustomButtonComponent, ToastComponent, GenerateElectricityJSONDialogComponent, GenerateGasJSONDialogComponent, CommonModule, IsoToFormattedDatePipe, DeleteConfirmationDialogComponent, IsoToFormattedPeriodPipe, CustomDropdownComponent, AdminNavigationComponent, AdminTopbarComponent],
  templateUrl: './admin-json-generator-page.component.html',
  styleUrl: './admin-json-generator-page.component.css'
})
export class AdminJsonGeneratorPageComponent implements OnInit {
  constructor(private router: Router, private cd: ChangeDetectorRef, private adminService: AdminService, private apiError: ApiErrorService, private translocoService: TranslocoService) { }

  @ViewChild(DeleteConfirmationDialogComponent) deleteConfirm!: DeleteConfirmationDialogComponent;

  electricityInvoices: InvoiceElectricityData[] = [];
  gasInvoices: InvoiceGasData[] = [];

  lastLoadedPageNumberElectricity: number = 0;
  loadMoreHiddenElectricity = false;

  lastLoadedPageNumberGas: number = 0;
  loadMoreHiddenGas = false;

  loadedElectricityInvoices: InvoiceElectricityData[] = [];
  loadedGasInvoices: InvoiceGasData[] = [];

  loadedElectricityResponse: JSONGeneratorHistoryElectricityGet[] = [];
  loadedGasResponse: JSONGeneratorHistoryGasGet[] = [];

  electricityResponse: JSONGeneratorHistoryElectricityGet[] = [];
  gasResponse: JSONGeneratorHistoryGasGet[] = [];
  electricityFetchedCount: number = 0;
  gasFetchedCount: number = 0;

  // File Upload
  @ViewChild('elFileInput') electricityFileInput!: ElementRef<HTMLInputElement>;
  selectedFilesElectricity: File[] = [];

  @ViewChild('gasFileInput') gasFileInput!: ElementRef<HTMLInputElement>;
  selectedFilesGas: File[] = [];

  // Filters
  filterDropdownOptions: string[] = [];

  selectedFilterDropdownElectricity: number | undefined;
  selectedFilterDropdownGas: number | undefined;

  ngOnInit(): void {
    this.cd.detectChanges();

    // Load Filter Translations
    const filterKeys = [
      'archive.filters.default',
      'admin.date_asc',
      'admin.date_desc',
      'admin.file_name_asc',
      'admin.file_name_desc',
      'admin.period_asc',
      'admin.period_desc',
      'admin.id_asc',
      'admin.id_desc',
      'admin.invoice_number_asc',
      'admin.invoice_number_desc'
    ];

    combineLatest(filterKeys.map((key) => this.translocoService.selectTranslate(key))).subscribe((translations) => {
      this.filterDropdownOptions = translations;
    });

    // Load Invoices First Page
    this.reloadElectricityInvoices();
    this.reloadGasInvoices();
  }

  @ViewChild(ToastComponent) toast!: ToastComponent;

  // Generate Electricity Dialog
  @ViewChild(GenerateElectricityJSONDialogComponent) generateElectricity!: GenerateElectricityJSONDialogComponent;

  // Generate Gas Dialog
  @ViewChild(GenerateGasJSONDialogComponent) generateGas!: GenerateGasJSONDialogComponent;

  goToDashboard() {
    this.router.navigate(["/dashboard"]);
  }

  openGenerateElectricityDialog() {
    this.generateElectricity.openModal();
  }

  openGenerateGasDialog() {
    this.generateGas.openModal();
  }

  loadElectricityHistoryNewPage() {
    this.adminService.getElectricityJsonRecords((this.lastLoadedPageNumberElectricity + 1)).subscribe({
      next: (response) => {
        if (response) {
          if (response.data.length === 0 || this.electricityFetchedCount + response.data.length >= response.totalCount) {
            this.loadMoreHiddenElectricity = true;
          }
          this.electricityFetchedCount += response.data.length;

          for (const item of response.data) {
            try {
              const castedItem: InvoiceElectricityData = item.json_history;
              this.loadedElectricityInvoices.push(castedItem);
              this.loadedElectricityResponse.push(item);
            } catch (e) {
              console.warn('Skipping invalid record in electricity data:', e, item);
              continue;
            }
          }

          // Applay filter
          this.resetElectricityInvoicesFilter();
          this.applySelectedFilterElectricity();
        }
      },
      error: async (error) => {
        var crid = 'UNKNOWN-CRID';

        if (error instanceof HttpErrorResponse) {
          crid = error.headers?.get('x-correlation-id') || 'UNKNOWN-CRID';
        }
        this.toast.showToast(await this.apiError.getErrorHeader(), await this.apiError.getErrorMessage(crid), 'error', 8000, false);
      },
      complete: () => {
        this.lastLoadedPageNumberElectricity++;
      }
    });
  }

  resetElectricityInvoicesFilter(resetFilter: boolean = false) {
    this.electricityInvoices = [];
    this.electricityInvoices = this.loadedElectricityInvoices;

    this.electricityResponse = [];
    this.electricityResponse = this.loadedElectricityResponse;


    if (resetFilter) {
      this.selectedFilterDropdownElectricity = undefined;
    }
  }

  reloadElectricityInvoices = () => {
    this.loadedElectricityInvoices = [];
    this.electricityResponse = [];
    this.loadedElectricityResponse = [];
    this.loadMoreHiddenElectricity = false;
    this.lastLoadedPageNumberElectricity = 0;
    this.electricityFetchedCount = 0;
    this.loadElectricityHistoryNewPage();
    this.resetElectricityInvoicesFilter();
  };

  loadGasHistoryNewPage() {
    this.adminService.getGasJsonRecords((this.lastLoadedPageNumberGas + 1)).subscribe({
      next: (response) => {
        if (response) {
          if (response.data.length === 0 || this.gasFetchedCount + response.data.length >= response.totalCount) {
            this.loadMoreHiddenGas = true;
          }
          this.gasFetchedCount += response.data.length;

          for (const item of response.data) {
            try {
              const castedItem: InvoiceGasData = item.json_history;
              this.loadedGasInvoices.push(castedItem);
              this.loadedGasResponse.push(item)
            } catch (e) {
              console.warn('Skipping invalid record in gas data:', e, item);
              continue;
            }
          }

          // Applay filter
          this.resetGasInvoicesFilter();
          this.applySelectedFilterGas();
        }
      },
      error: async (error) => {
        var crid = 'UNKNOWN-CRID';

        if (error instanceof HttpErrorResponse) {
          crid = error.headers?.get('x-correlation-id') || 'UNKNOWN-CRID';
        }
        this.toast.showToast(await this.apiError.getErrorHeader(), await this.apiError.getErrorMessage(crid), 'error', 8000, false);
      },
      complete: () => {
        this.lastLoadedPageNumberGas++;
      }
    });
  }

  resetGasInvoicesFilter(resetFilter: boolean = false) {
    this.gasInvoices = [];
    this.gasInvoices = this.loadedGasInvoices;

    this.gasResponse = [];
    this.gasResponse = this.loadedGasResponse;

    if (resetFilter) {
      this.selectedFilterDropdownGas = undefined;
    }
  }

  reloadGasInvoices = () => {
    this.loadedGasInvoices = [];
    this.gasResponse = [];
    this.loadedGasResponse = [];
    this.loadMoreHiddenGas = false;
    this.lastLoadedPageNumberGas = 0;
    this.gasFetchedCount = 0;
    this.loadGasHistoryNewPage();
    this.resetGasInvoicesFilter();
  };

  reloadCallbackFunctionElectricity = () => this.reloadElectricityInvoices();
  reloadCallbackFunctionGas = () => this.reloadGasInvoices();

  async removeElectricityInvoice(index: number) {
    var response = await this.deleteConfirm.openModal()
    if (!response) {
      return;
    }

    try {
      await lastValueFrom(this.adminService.deleteElectricityJsonRecord(this.electricityResponse[index].id));
    }
    catch (error) {
      var crid = 'UNKNOWN-CRID';
      if (error instanceof HttpErrorResponse) {
        crid = error.headers?.get('x-correlation-id') || 'UNKNOWN-CRID';
      }
      this.toast.showToast(await this.apiError.getErrorHeader(), await this.apiError.getErrorMessage(crid), 'error', 8000, false);
      return;
    }
    this.toast.showToast('shared.success', 'admin.record_deleted', 'success', 5000, true);
    this.reloadElectricityInvoices();
  }

  async removeGasInvoice(index: number) {
    var response = await this.deleteConfirm.openModal()
    if (!response) {
      return;
    }


    try {
      await lastValueFrom(this.adminService.deleteGasJsonRecord(this.gasResponse[index].id));
    }
    catch (error) {
      var crid = 'UNKNOWN-CRID';
      if (error instanceof HttpErrorResponse) {
        crid = error.headers?.get('x-correlation-id') || 'UNKNOWN-CRID';
      }
      this.toast.showToast(await this.apiError.getErrorHeader(), await this.apiError.getErrorMessage(crid), 'error', 8000, false);
      return;
    }

    this.reloadGasInvoices();
  }

  editElectricityInvoice(index: number) {
    this.generateElectricity.openModal(this.electricityInvoices[index], this.electricityResponse[index])
  }

  editGasInvoice(index: number) {
    this.generateGas.openModal(this.gasInvoices[index], this.gasResponse[index])
  }

  triggerElectricityFileInput() {
    this.electricityFileInput.nativeElement.click();
  }

  async onElectricityFileSelected(event: Event): Promise<void> {
    const fileInput = event.target as HTMLInputElement;
    const files = fileInput?.files;

    if (!files || files.length === 0) return;

    for (const file of Array.from(files)) {
      try {
        await lastValueFrom(this.adminService.uploadElectricityJsonRecord(file));
        this.toast.showToast(this.translocoService.translate("shared.file_upload.successful"), this.translocoService.translate("admin.file_uploaded", { fileName: file.name }), "success", 4000, false);
      } catch (error) {
        var showError = true;
        let crid = 'UNKNOWN-CRID';

        if (error instanceof HttpErrorResponse) {
          crid = error.headers?.get('x-correlation-id') || 'UNKNOWN-CRID';

          if (error.error.code === 'SVC-4006') {
            showError = false;
            this.toast.showToast('shared.error', 'admin.invalid_json', 'error', 5000, true);
          }
          else if (error.error.code === 'SVC-4000') {
            showError = false;
            this.toast.showToast('shared.error', 'admin.invalid_json', 'error', 5000, true);
          }
        }

        if (showError) {
          this.toast.showToast(await this.apiError.getErrorHeader(), await this.apiError.getErrorMessage(crid), 'error', 8000, false);
        }
        continue;
      }
    }

    // Reset file input and allow selectin new files
    fileInput.value = '';

    this.reloadElectricityInvoices();
  }

  triggerGasFileInput() {
    this.gasFileInput.nativeElement.click();
  }

  async onGasFileSelected(event: Event): Promise<void> {
    const fileInput = event.target as HTMLInputElement;
    const files = fileInput?.files;

    if (!files || files.length === 0) return;

    for (const file of Array.from(files)) {
      try {
        await lastValueFrom(this.adminService.uploadGasJsonRecord(file));
        this.toast.showToast(this.translocoService.translate("shared.file_upload.successful"), this.translocoService.translate("admin.file_uploaded", { fileName: file.name }), "success", 4000, false);
      } catch (error) {
        var showError = true;
        let crid = 'UNKNOWN-CRID';

        if (error instanceof HttpErrorResponse) {
          crid = error.headers?.get('x-correlation-id') || 'UNKNOWN-CRID';

          if (error.error.code === 'SVC-4006') {
            showError = false;
            this.toast.showToast('shared.error', 'admin.invalid_json', 'error', 5000, true);
          }
          else if (error.error.code === 'SVC-4000') {
            showError = false;
            this.toast.showToast('shared.error', 'admin.invalid_json', 'error', 5000, true);
          }
        }

        if (showError) {
          this.toast.showToast(await this.apiError.getErrorHeader(), await this.apiError.getErrorMessage(crid), 'error', 8000, false);
        }
        continue;
      }
    }

    // Reset file input and allow selectin new files
    fileInput.value = '';

    this.reloadGasInvoices();
  }

  applySelectedFilterElectricity() {
    if (this.selectedFilterDropdownElectricity === undefined) {
      return;
    }

    const optionIndex = this.selectedFilterDropdownElectricity;

    // Combine invoice data and response
    const combined = this.electricityInvoices.map((invoice, idx) => ({
      invoice: invoice,
      response: this.electricityResponse[idx]
    }));

    if (optionIndex === 0) {
      this.resetElectricityInvoicesFilter();
    }
    // Processed at ASC/DESC
    else if (optionIndex === 1) {
      combined.sort((a, b) =>
        new Date(a.invoice?.processed_at ?? '').getTime() -
        new Date(b.invoice?.processed_at ?? '').getTime()
      );
    } else if (optionIndex === 2) {
      combined.sort((a, b) =>
        new Date(b.invoice?.processed_at ?? '').getTime() -
        new Date(a.invoice?.processed_at ?? '').getTime()
      );
    }
    // File Name ASC/DESC
    else if (optionIndex === 3) {
      combined.sort((a, b) =>
        (a.response?.file_name ?? '').localeCompare(b.response?.file_name ?? '')
      );
    } else if (optionIndex === 4) {
      combined.sort((a, b) =>
        (b.response?.file_name ?? '').localeCompare(a.response?.file_name ?? '')
      );
    }
    // For period ASC/DESC
    else if (optionIndex === 5) {
      combined.sort((a, b) =>
        new Date(a.invoice?.data?.invoice_data?.for_period ?? '').getTime() -
        new Date(b.invoice?.data?.invoice_data?.for_period ?? '').getTime()
      );
    } else if (optionIndex === 6) {
      combined.sort((a, b) =>
        new Date(b.invoice?.data?.invoice_data?.for_period ?? '').getTime() -
        new Date(a.invoice?.data?.invoice_data?.for_period ?? '').getTime()
      );
    }
    // ID ASC/DESC
    else if (optionIndex === 7) {
      combined.sort((a, b) =>
        (a.invoice?.id ?? '').localeCompare(b.invoice?.id ?? '')
      );
    } else if (optionIndex === 8) {
      combined.sort((a, b) =>
        (b.invoice?.id ?? '').localeCompare(a.invoice?.id ?? '')
      );
    }
    // Invoice number ASC/DESC
    else if (optionIndex === 9) {
      combined.sort((a, b) =>
        (a.invoice?.data?.invoice_data?.invoice_number ?? '').localeCompare(b.invoice?.data?.invoice_data?.invoice_number ?? '')
      );
    } else if (optionIndex === 10) {
      combined.sort((a, b) =>
        (b.invoice?.data?.invoice_data?.invoice_number ?? '').localeCompare(a.invoice?.data?.invoice_data?.invoice_number ?? '')
      );
    }

    this.electricityInvoices = combined.map(item => item.invoice);
    this.electricityResponse = combined.map(item => item.response);
  }

  applySelectedFilterGas() {
    if (this.selectedFilterDropdownGas === undefined) {
      return;
    }

    const optionIndex = this.selectedFilterDropdownGas;

    // Combine invoice data and response
    const combined = this.gasInvoices.map((invoice, idx) => ({
      invoice: invoice,
      response: this.gasResponse[idx]
    }));

    if (optionIndex === 0) {
      this.resetGasInvoicesFilter();
    }
    // Processed at ASC/DESC
    else if (optionIndex === 1) {
      combined.sort((a, b) =>
        new Date(a.invoice?.processed_at ?? '').getTime() -
        new Date(b.invoice?.processed_at ?? '').getTime()
      );
    } else if (optionIndex === 2) {
      combined.sort((a, b) =>
        new Date(b.invoice?.processed_at ?? '').getTime() -
        new Date(a.invoice?.processed_at ?? '').getTime()
      );
    }
    // File Name ASC/DESC
    else if (optionIndex === 3) {
      combined.sort((a, b) =>
        (a.response?.file_name ?? '').localeCompare(b.response?.file_name ?? '')
      );
    } else if (optionIndex === 4) {
      combined.sort((a, b) =>
        (b.response?.file_name ?? '').localeCompare(a.response?.file_name ?? '')
      );
    }
    // For period ASC/DESC
    else if (optionIndex === 5) {
      combined.sort((a, b) =>
        new Date(a.invoice?.data?.invoice_data?.for_period ?? '').getTime() -
        new Date(b.invoice?.data?.invoice_data?.for_period ?? '').getTime()
      );
    } else if (optionIndex === 6) {
      combined.sort((a, b) =>
        new Date(b.invoice?.data?.invoice_data?.for_period ?? '').getTime() -
        new Date(a.invoice?.data?.invoice_data?.for_period ?? '').getTime()
      );
    }
    // ID ASC/DESC
    else if (optionIndex === 7) {
      combined.sort((a, b) =>
        (a.invoice?.id ?? '').localeCompare(b.invoice?.id ?? '')
      );
    } else if (optionIndex === 8) {
      combined.sort((a, b) =>
        (b.invoice?.id ?? '').localeCompare(a.invoice?.id ?? '')
      );
    }
    // Invoice number ASC/DESC
    else if (optionIndex === 9) {
      combined.sort((a, b) =>
        (a.invoice?.data?.invoice_data?.invoice_number ?? '').localeCompare(b.invoice?.data?.invoice_data?.invoice_number ?? '')
      );
    } else if (optionIndex === 10) {
      combined.sort((a, b) =>
        (b.invoice?.data?.invoice_data?.invoice_number ?? '').localeCompare(a.invoice?.data?.invoice_data?.invoice_number ?? '')
      );
    }

    this.gasInvoices = combined.map(item => item.invoice);
    this.gasResponse = combined.map(item => item.response);
  }

}